import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Fetch a GET endpoint with a few retries to survive transient failures
// (cold starts, brief network/CORS blips) instead of failing the whole page.
async function getWithRetry(path, retries = 2) {
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await api.get(path);
    } catch (e) {
      lastErr = e;
      if (attempt < retries) await sleep(600 * (attempt + 1));
    }
  }
  throw lastErr;
}

// Loads the published knowledge graph: flat node list + edges (both endpoints published).
export function useGraphData() {
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [nodeMap, setNodeMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refetch = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // Nodes are critical for the doorways/graph; edges are secondary.
        // Load them independently so a failed edges call still renders nodes.
        const browseRes = await getWithRetry("/kg/browse");
        const flat = [];
        browseRes.data.forEach((g) => {
          g.nodes.forEach((n) =>
            flat.push({ id: n.id, type: g.type, label: n.label, description: n.description || "" })
          );
        });
        const map = {};
        flat.forEach((n) => (map[n.id] = n));
        const pub = new Set(flat.map((n) => n.id));

        let es = [];
        try {
          const edgeRes = await getWithRetry("/kg/edges");
          es = edgeRes.data
            .filter((e) => pub.has(e.source_id) && pub.has(e.target_id))
            .map((e) => ({
              id: e.id,
              source: e.source_id,
              target: e.target_id,
              type: e.type,
              label: e.label,
              props: e.props || {},
            }));
        } catch (edgeErr) {
          // Non-fatal: render nodes without connections rather than blank the page.
          console.warn("kg/edges failed; rendering graph without edges", edgeErr);
        }

        if (!alive) return;
        setNodes(flat);
        setNodeMap(map);
        setEdges(es);
      } catch (e) {
        if (alive) setError("Could not load the knowledge graph. Please refresh.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [reloadKey]);

  return { nodes, edges, nodeMap, loading, error, refetch };
}
